package com.neurobs.js.ccab

import com.neurobs.ccab._
import com.neurobs.ccab.db._
import com.neurobs.js.http
import com.neurobs.js.view.Form
import com.neurobs.util.JsonPrinter.jsonPrint
import io.circe.generic.auto._
import io.circe.{Decoder, Encoder}
import org.scalajs.dom.ext.Ajax

object CCABServer {
	implicit val sexDecoder = Decoder.decodeEnumeration(Sex)
	implicit val sexEncoder = Encoder.encodeEnumeration(Sex)
	implicit val keyStorageTypeDecoder = Decoder.decodeEnumeration(KeyStorageType)
	implicit val keyStorageTypeEncoder = Encoder.encodeEnumeration(KeyStorageType)
	import DatabaseJson._

	def makeUrlSuffix(args: Seq[(String, Option[Any])]): String = {
		val strings = args map { a => a._2 match {
			case None => None
			case Some(value) => Some(s"${a._1}=$value")
		}}
		val parameters = strings.flatten.mkString("&")
		if (parameters.isEmpty) "" else s"?$parameters"
	}

	def serverInfo(service: String)(onComplete: (http.ServerObjectResponse[ObjectResult[ServerInfo]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ObjectResult[ServerInfo]](
			url = s"/server_info/$service",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def login(info: LoginData)(onComplete: (http.ServerObjectResponse[Option[ValidUserData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Option[ValidUserData]](
			url = "/api/v1/login",
			t = http.HTTPRequestType.Post,
			data = jsonPrint(info)
		)(onComplete)
	}

	def loginStatus(onComplete: (http.ServerObjectResponse[Option[ValidUserData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Option[ValidUserData]](
			url = "/api/v1/login_status",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getKeys(onComplete: (http.ServerObjectResponse[Seq[KeyData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[KeyData]](
			url = "/api/v1/keys",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getErrorClasses(onComplete: (http.ServerObjectResponse[Seq[ErrorClassData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[ErrorClassData]](
			url = "/api/v1/error_classes",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getErrorTypes(onComplete: (http.ServerObjectResponse[Seq[ErrorTypeData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[ErrorTypeData]](
			url = "/api/v1/error_types",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getASREngines(onComplete: (http.ServerObjectResponse[Seq[AsrEngineData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[AsrEngineData]](
			url = "/api/v1/asr_engines",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def createError(data: ErrorCreateData)(onComplete: (http.ServerObjectResponse[ErrorData]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ErrorData](
			url = "/api/v1/errors",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def searchErrors(data: ErrorSearchData)(onComplete: (http.ServerObjectResponse[Seq[ErrorData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[ErrorData]](
			url = "/api/v1/errors/search",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def getError(id: Long)(onComplete: (http.ServerObjectResponse[ObjectResult[Option[ErrorData]]] => Unit)) = {
		http.getServerObjectAsync[ObjectResult[Option[ErrorData]]](
			url = s"/api/v1/errors/$id",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def searchEventServerEvents(data: EventServerEventSearchData)(onComplete: (http.ServerObjectResponse[Seq[EventServerEventData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[EventServerEventData]](
			url = "/api/v1/event_server_event/search",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def getFileTranscript(id: Long)(onComplete: (http.ServerObjectResponse[FileTranscriptData]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[FileTranscriptData](
			url = s"/api/v1/file_transcripts/$id",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getSpeechAudioFile(id: Long)(onComplete: (http.ServerObjectResponse[SpeechAudioFileData]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[SpeechAudioFileData](
			url = s"/api/v1/speech_audio_files/$id",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def searchFileTranscriptRequests(data: FileTranscriptRequestSearchData)(onComplete: (http.ServerObjectResponse[Seq[(FileTranscriptRequestData, Option[FileTranscriptResponseData])]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[(FileTranscriptRequestData, Option[FileTranscriptResponseData])]](
			url = "/api/v1/file_transcript_requests/search",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def searchTestTranscripts(data: TestTranscriptSearchData)(onComplete: (http.ServerObjectResponse[Seq[TestTranscriptMetaData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[TestTranscriptMetaData]](
			url = "/api/v1/test_transcripts/search",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def getFileTranscriptUnprocessedCounts()(onComplete: (http.ServerObjectResponse[TranscriptTotalUnprocessed]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[TranscriptTotalUnprocessed](
			url = "/api/v1/file_transcript_requests/unprocessed_counts",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getFileTranscriptRequestTotals(data: FileTranscriptTotalsRequest)(onComplete: (http.ServerObjectResponse[Seq[FileTranscriptIntervalTotals]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[FileTranscriptIntervalTotals]](
			url = "/api/v1/file_transcript_requests/totals",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def getTestTranscript(testTranscriptId: Long)(onComplete: http.ServerObjectResponse[TestTranscriptData] => Unit): http.RequestProcess = {
		http.getServerObjectAsync[TestTranscriptData](
			url = s"/api/v1/test_transcripts/$testTranscriptId",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getUserRoles(userId: Long)(onComplete: (http.ServerObjectResponse[Seq[RoleData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[RoleData]](
			url = s"/api/v1/users/$userId/roles",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def checkDataPassword(accountId: Long, dataPassword: String)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = s"/api/v1/accounts/$accountId/data_password/check?data_password=$dataPassword",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getSettings(onComplete: (http.ServerObjectResponse[Seq[SettingData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[SettingData]](
			url = "/api/v1/settings",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getSettingValues(settingId: Option[Long], userId: Option[Long], accountId: Option[Long])(onComplete: (http.ServerObjectResponse[Seq[SettingValueData]]) => Unit): http.RequestProcess = {
		val suffix = makeUrlSuffix(Seq(
			("setting_id", settingId),
			("user_id", userId),
			("account_id", accountId)
		))
		http.getServerObjectAsync[Seq[SettingValueData]](
			url = s"/api/v1/setting_values$suffix",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def setSettingValue(data: SettingValueCreateData)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = "/api/v1/setting_values",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def resetUserPassword(userId: Long, data: ResetUserPasswordData)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = s"/api/v1/users/$userId/password",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Put
		)(onComplete)
	}

	def createTest(data: TestUpdateData)(onComplete: (http.ServerObjectResponse[ObjectResult[TestData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ObjectResult[TestData]](
			url = "/api/v1/tests",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def createStudyType(data: StudyTypeUpdateData)(onComplete: (http.ServerObjectResponse[ObjectResult[StudyTypeData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ObjectResult[StudyTypeData]](
			url = "/api/v1/study_types",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def getTests(onComplete: (http.ServerObjectResponse[Seq[TestData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[TestData]](
			url = "/api/v1/tests",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getStudyTypes(onComplete: (http.ServerObjectResponse[Seq[StudyTypeData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[StudyTypeData]](
			url = "/api/v1/study_types",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getControllerSessions(onComplete: (http.ServerObjectResponse[Seq[ControllerSessionData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[ControllerSessionData]](
			url = "/api/v1/controller_sessions",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getControllerSessionToken(sessionId: String)(onComplete: (http.ServerObjectResponse[ControllerSessionTokenData]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ControllerSessionTokenData](
			url = s"/api/v1/controller_sessions/token?session_id=${http.urlEncode(sessionId)}",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getReleaseTypes(onComplete: (http.ServerObjectResponse[Seq[ReleaseTypeData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[ReleaseTypeData]](
			url = "/api/v1/release_types",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def deleteTest(testId: Long)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = s"/api/v1/tests/$testId",
			t = http.HTTPRequestType.Delete
		)(onComplete)
	}

	def deleteStudyType(studyTypeId: Long)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = s"/api/v1/study_types/$studyTypeId",
			t = http.HTTPRequestType.Delete
		)(onComplete)
	}

	def deleteRelease(releaseId: Long)(onComplete: (Option[http.ServerError]) => Unit): http.RequestProcess = {
		http.serverCallAsyncNoResponse(
			url = s"/api/v1/releases/$releaseId",
			t = http.HTTPRequestType.Delete
		)(onComplete)
	}

	def updateTest(testId: Long, data: TestUpdateData)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = s"/api/v1/tests/$testId",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Put
		)(onComplete)
	}

	def updateStudyType(studyTypeId: Long, data: StudyTypeUpdateData)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = s"/api/v1/study_types/$studyTypeId",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Put
		)(onComplete)
	}

	def updateRelease(releaseId: Long, data: ReleaseUpdateData)(onComplete: (Option[http.ServerError]) => Unit): http.RequestProcess = {
		http.serverCallAsyncNoResponse(
			url = s"/api/v1/releases/$releaseId",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Put
		)(onComplete)
	}

	def getReleases(releaseType: String, all: Boolean)(onComplete: (http.ServerObjectResponse[Seq[ReleaseDataRest]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[ReleaseDataRest]](
			url = s"/api/v1/releases/$releaseType?all=$all",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getLatestSoftwareRelease(onComplete: (http.ServerObjectResponse[Option[ReleaseDataRest]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Option[ReleaseDataRest]](
			url = s"/api/v1/releases/controller_installers/latest",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def uploadSessionLog(subjectGuid: String, keyIdentifier: String, file: Ajax.InputData)(onComplete: http.ServerObjectResponse[GeneralResult] => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = s"/api/v1/test_sessions/log/admin?subject_guid=$subjectGuid&key_identifier=$keyIdentifier",
			data = file,
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def createRelease(releaseType: String, version: String, dependencyMinVersion: String, fileExtension: String, file: Ajax.InputData)(onComplete: (http.ServerObjectResponse[CreateReleaseResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[CreateReleaseResult](
			url = s"/api/v1/releases/$releaseType?version=$version&file_extension=$fileExtension&dependency_min_version=$dependencyMinVersion",
			data = file,
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def createStudy(data: StudyUpdateData, accountId: Long)(onComplete: (http.ServerObjectResponse[ObjectResult[StudyData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ObjectResult[StudyData]](
			url = s"/api/v1/studies?account_id=$accountId",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def getStudies(accountId: Long)(onComplete: (http.ServerObjectResponse[Seq[StudyData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[StudyData]](
			url = s"/api/v1/studies?account_id=$accountId",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getAllStudies(onComplete: http.ServerObjectResponse[ObjectResult[Seq[StudyData]]] => Unit) = {
		http.getServerObjectAsync[ObjectResult[Seq[StudyData]]](
			url = "/api/v1/studies/all",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getStudyViews(accountId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[StudyView]]] => Unit): http.RequestProcess = {
		val suffix = makeUrlSuffix(Seq(
			("account_id", Some(accountId))
		))
		http.getServerObjectAsync[ObjectResult[Seq[StudyView]]](
			url = s"/api/v1/studies/views$suffix",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def updateSessions(data: UpdateSessionsData)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = "/api/v1/test_sessions/update",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Put
		)(onComplete)
	}

	def updateStudy(id: Long, data: StudyUpdateData)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = s"/api/v1/studies/$id",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Put
		)(onComplete)
	}

	def deleteStudy(id: Long, accountId: Long)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = s"/api/v1/studies/$id?account_id=$accountId",
			t = http.HTTPRequestType.Delete
		)(onComplete)
	}

	def getSubjects(accountId: Long, dataPassword: String)(onComplete: (http.ServerObjectResponse[ObjectResult[Seq[AccountSubjectResponse]]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ObjectResult[Seq[AccountSubjectResponse]]](
			url = s"/api/v1/accounts/$accountId/subjects?data_password=$dataPassword",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getTestSessions(accountId: Long)(onComplete: (http.ServerObjectResponse[Seq[db.SessionData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[db.SessionData]](
			url = s"/api/v1/accounts/$accountId/test_sessions",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getTestSessionViews(search: SessionSearchData)(onComplete: (http.ServerObjectResponse[ObjectResult[SessionSearchResultData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ObjectResult[SessionSearchResultData]](
			url = "/api/v1/test_sessions/search",
			data = jsonPrint(search),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def getSubjectSessions(data: SubjectSessionsRequestData)(onComplete: (http.ServerObjectResponse[ObjectResult[SubjectSessionsData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ObjectResult[SubjectSessionsData]](
			url = "/api/v1/test_sessions/subject",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def getAccountSubjectTestSessions(accountSubjectId: Long)(onComplete: (http.ServerObjectResponse[Seq[db.SessionData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[db.SessionData]](
			url = s"/api/v1/account_subjects/$accountSubjectId/test_sessions",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getTestRuns(testSessionId: Long)(onComplete: (http.ServerObjectResponse[Seq[TestRunData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[TestRunData]](
			url = s"/api/v1/test_sessions/$testSessionId/test_runs",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getTestRunLogfilesInTimeframe(timeframeSeconds: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[TestRunLogfileView]]] => Unit) = {
		val suffix = makeUrlSuffix(Seq(("timeframe_seconds", Some(timeframeSeconds))))
		http.getServerObjectAsync[ObjectResult[Seq[TestRunLogfileView]]](
			url = s"/api/v1/test_runs/timeframe$suffix",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getSessionLog(sessionId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[String]] => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ObjectResult[String]](
			url = s"/api/v1/test_sessions/$sessionId/log",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getTestRunTranscriptStatus(logfileFilename: String)(onComplete: (http.ServerObjectResponse[ObjectResult[TranscriptSyncManager.TestRunSyncStatus]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ObjectResult[TranscriptSyncManager.TestRunSyncStatus]](
			url = s"/api/v1/test_runs/transcript_status?logfile=${http.urlEncode(logfileFilename)}",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getSessionTranscriptStatus(sessionId: Long)(onComplete: http.ServerObjectResponse[TranscriptSyncManager.MissingSessionTranscriptData] => Unit): http.RequestProcess = {
		http.getServerObjectAsync[TranscriptSyncManager.MissingSessionTranscriptData](
			url = s"/api/v1/test_sessions/$sessionId/transcript_status",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getStudyTranscriptStatus(studyId: Long)(onComplete: http.ServerObjectResponse[Seq[TranscriptSyncManager.MissingSessionTranscriptData]] => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[TranscriptSyncManager.MissingSessionTranscriptData]](
			url = s"/api/v1/studies/$studyId/transcript_status",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getErrorTestRuns(data: TestRunSearchData)(onComplete: (http.ServerObjectResponse[Seq[TestRunView]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[TestRunView]](
			url = s"/api/v1/test_runs/errors",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def getLogfiles(testRunId: Long)(onComplete: (http.ServerObjectResponse[Seq[LogfileData]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[Seq[LogfileData]](
			url = s"/api/v1/test_runs/$testRunId/logfiles",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def createSubject(accountId: Long, data: SubjectCreateData)(onComplete: (http.ServerObjectResponse[ObjectResult[AccountSubjectResponse]]) => Unit): http.RequestProcess = {
		println(s"createSubject data: $data")
		println(s"json: ${jsonPrint(data)}")

		http.getServerObjectAsync[ObjectResult[AccountSubjectResponse]](
			url = s"/api/v1/accounts/$accountId/subjects",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def updateSubject(accountSubjectId: Long, data: SubjectUpdateData)(onComplete: (http.ServerObjectResponse[GeneralResult]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[GeneralResult](
			url = s"/api/v1/account_subjects/$accountSubjectId",
			data = jsonPrint(data),
			t = http.HTTPRequestType.Put
		)(onComplete)
	}

	def getTestAnalyses(testRunId: Long, required: Boolean)(onComplete: (http.ServerObjectResponse[ObjectResult[Seq[TestAnalysisData]]]) => Unit): http.RequestProcess = {
		http.getServerObjectAsync[ObjectResult[Seq[TestAnalysisData]]](
			url = s"/api/v1/test_runs/$testRunId/test_analyses?required=$required",
			t = http.HTTPRequestType.Post
		)(onComplete)
	}

	def getTestAnalysisWithinTimeframe(timeframeSeconds: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[TestAnalysisData]]] => Unit) = {
		val suffix = makeUrlSuffix(Seq(("timeframe_seconds", Some(timeframeSeconds))))
		http.getServerObjectAsync[ObjectResult[Seq[TestAnalysisData]]](
			url = s"/api/v1/test_runs/test_analysis/timeframe$suffix",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getEventServerEvent(id: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Option[EventServerEventData]]] => Unit) = {
		http.getServerObjectAsync[ObjectResult[Option[EventServerEventData]]](
			url = s"/api/v1/event_server_event/$id",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getEventServerEventsInTimeframe(timeframeSeconds: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[EventServerGeneralEventData]]] => Unit) = {
		val suffix = makeUrlSuffix(Seq(("timeframe_seconds", Some(timeframeSeconds))))
		http.getServerObjectAsync[ObjectResult[Seq[EventServerGeneralEventData]]](
			url = s"/api/v1/event_server_event/timeframe$suffix",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}

	def getRetryEventsFromId(id: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[EventServerEventData]]] => Unit) = {
		http.getServerObjectAsync[ObjectResult[Seq[EventServerEventData]]](
			url = s"/api/v1/event_server_event/retries/$id",
			t = http.HTTPRequestType.Get
		)(onComplete)
	}


	def generalResultHandler(formCallback: Form.OnProcessComplete)(onSuccess: () => Unit): (http.ServerObjectResponse[GeneralResult]) => Unit = {
		(res: http.ServerObjectResponse[GeneralResult]) =>
			res match {
				case Left(error) => formCallback(Some(error.message))
				case Right(obj) => obj.error match {
					case Some(error) => formCallback(Some(error))
					case None =>
						formCallback(None)
						onSuccess()
				}
			}
	}

	def objectResultHandler[T](formCallback: Form.OnProcessComplete)(onSuccess: (Option[T]) => Unit): (http.ServerObjectResponse[ObjectResult[T]]) => Unit = {
		(res: http.ServerObjectResponse[ObjectResult[T]]) =>
			res match {
				case Left(error) => formCallback(Some(error.message))
				case Right(obj) => obj.error match {
					case Some(error) => formCallback(Some(error))
					case None =>
						formCallback(None)
						onSuccess(obj.data)
				}
			}
	}

	def handleObjectResult[T](handler: Either[String, T] => Unit): (http.ServerObjectResponse[ObjectResult[T]]) => Unit = {
		(res: http.ServerObjectResponse[ObjectResult[T]]) =>
			val arg = res match {
				case Left(error) => Left(error.message)
				case Right(obj) => obj.error match {
					case Some(error) => Left(error)
					case None =>
						obj.data match {
							case None => Left("The response contained no error and no data")
							case Some(data) => Right(data)
						}
				}
			}
			handler(arg)
	}


}
